/**
 * "Download template" (design 2026-10-02 section 8): for each class and
 * kind, a CSV of the header row alone, in the columns the design's section 4
 * lays out - LIV's upper-case names, the canonical ones (headers match
 * case-insensitively, ignoring spaces and underscores, and camelCase aliases
 * are accepted too). Optional columns are included, so a template shows
 * every column a file may carry; a blank optional cell is read as absent.
 *
 * A NAV file and a bank statement keep the prototype's grids
 * (intake/nav-file.js, intake/statement-file.js). A daily file's template
 * is named as LIV names the file, and a NAV file's, a statement's and a
 * calendar's by their own word, so File Uploads recognises the filled-in
 * file when it comes back (uploadFile.js guessUpload); an initial load's is
 * named for what it is, and its class and kind are chosen by hand (the
 * design guesses only the names above).
 *
 * The wealth-managed (WM) clients' files (migration 0026) take the columns
 * of the holdings or transactions file each mirrors, under a wm-* name that
 * File Uploads recognises as the WM file - never as the register's own.
 *
 * The analytics files (migration 0014) are kept as uploaded, whatever their
 * columns; their templates are the layouts LIV exports them in, named as
 * exported less the date, so each comes back as its class. A fund's fact
 * sheet is a Category and its Details on each row: its template is those two
 * headers.
 */
import { CLASS_GROUPS, CLASS_LABEL, FILE_CLASS_KEYS, STORED_CLASSES, groupOf, takesLoadKind } from './uploadKinds.js';

/** The Core's own files' columns, in file order. */
const CORE_COLUMNS = Object.freeze({
  // IP-179: chargeBase, what the fund's transaction charge rate is applied on.
  NAV_VALUES: ['fundCode', 'navValue', 'issuedUnits', 'chargeBase'],
  BANK_STATEMENT: ['referenceId', 'amount', 'valueDate', 'description', 'channel'],
  // 4.1
  'CALENDAR:INITIAL': ['DATE', 'DAY_TYPE', 'HOLIDAY_NAME', 'IS_PUBLIC', 'SOURCE', 'SOURCE_REF'],
  'CALENDAR:DAILY': ['DATE', 'DAY_TYPE', 'HOLIDAY_NAME', 'IS_PUBLIC', 'SOURCE', 'SOURCE_REF'],
  // 4.2: price history only.
  'UNIT_PRICES:INITIAL': ['DATE', 'FUND_ID', 'NAV_PER_UNIT_PRICE', 'SELLING_PRICE', 'BUYING_PRICE', 'BUYING_PRICE_WITH_EXIT_FEE', 'NAV', 'CLOSING_UNITS'],
  // 4.4: one row per lot.
  'HOLDINGS:INITIAL': ['CLIENT_ID', 'STRATEGY_ID', 'FUND_ID', 'UNITS', 'COST', 'ACQUIRED_DATE', 'LOT_REF'],
  // 4.5: LIV's daily-client-holdings.
  'HOLDINGS:DAILY': ['CLIENT_ID', 'STRATEGY_ID', 'FUND_ID', 'TOTAL_QUANTITY', 'QUANTITY_LESS_THAN_1_YEAR', 'QUANTITY_MORE_THAN_1_YEAR', 'COST_TOTAL_VALUE'],
  // 4.6: history.
  'TRANSACTIONS:INITIAL': ['TRANSACTION_ID', 'DATE', 'FUND_ID', 'CLIENT_ID', 'STRATEGY_ID', 'TYPE', 'USED_PRICE', 'QUANTITY', 'AMOUNT', 'REF_ID', 'FEE'],
  // 4.7: as 4.6 without DATE - every row is dealt today.
  'TRANSACTIONS:DAILY': ['TRANSACTION_ID', 'FUND_ID', 'CLIENT_ID', 'STRATEGY_ID', 'TYPE', 'USED_PRICE', 'QUANTITY', 'AMOUNT', 'REF_ID', 'FEE'],
});

/** Each fund's security holdings, as LIV exports them - the same columns for every fund. */
const SECURITY_HOLDINGS = Object.freeze(['FUND_ID', 'SECURITY_ID', 'SECURITY_TYPE', 'DAYS_TO_MATURITY', 'RATING_AGENCY', 'RATING', 'GICS_SECTOR',
  'NET_SECURITY_VALUE']);

/** A fund's fact sheet: a Category and its Details on each row. */
const FUND_FACTS = Object.freeze(['Category', 'Details']);

/** A per-fund analytics class's fund (SECURITIES_UTIF -> UTIF), or null. */
const fundOf = (fileClass) => /^(?:SECURITIES|FUND_FACTS)_(.+)$/.exec(fileClass)?.[1] ?? null;

const SECURITIES_CLASSES = STORED_CLASSES.filter((c) => c.startsWith('SECURITIES_'));
const FUND_FACTS_CLASSES = STORED_CLASSES.filter((c) => c.startsWith('FUND_FACTS_'));

/** The columns of each registry key (intake/kinds.js registryKey), in file order: a WM file's are the file's it mirrors. */
export const TEMPLATE_COLUMNS = Object.freeze({
  ...CORE_COLUMNS,
  'WM_HOLDINGS:INITIAL': CORE_COLUMNS['HOLDINGS:INITIAL'],
  'WM_HOLDINGS:DAILY': CORE_COLUMNS['HOLDINGS:DAILY'],
  'WM_TRANSACTIONS:INITIAL': CORE_COLUMNS['TRANSACTIONS:INITIAL'],
  'WM_TRANSACTIONS:DAILY': CORE_COLUMNS['TRANSACTIONS:DAILY'],
  // Migration 0014: the analytics files, as LIV exports them.
  BENCHMARK: ['SP_20', 'CRISIL_(91_DAYS)', 'CRISIL_(364_DAYS)', 'SP20TR'],
  INCOME_EXPENSE: ['FUND_ID', 'TOTAL_INTEREST_INCOME_PER_DAY', 'CAPITAL_GAIN_ON_FIXED_INCOME_PER_DAY', 'DIVIDEND_INCOME',
    'CAPITAL_GAIN_ON_EQUITY_PER_DAY', 'TOTAL_EXPENSES_PER_DAY', 'TOTAL_WHT_ON_INT_INCOME_PER_DAY', 'WHT_ON_DIVIDEND_INCOME_PER_DAY',
    'TOTAL_OTHER_INCOME_PER_DAY'],
  ...Object.fromEntries(SECURITIES_CLASSES.map((c) => [c, SECURITY_HOLDINGS])),
  ...Object.fromEntries(FUND_FACTS_CLASSES.map((c) => [c, FUND_FACTS])),
});

/** Each template's file name. */
const TEMPLATE_NAMES = Object.freeze({
  NAV_VALUES: 'nav_values.csv',
  BANK_STATEMENT: 'bank_transaction.csv',
  'CALENDAR:INITIAL': 'calendar-initial-load.csv',
  'CALENDAR:DAILY': 'calendar-daily.csv',
  'UNIT_PRICES:INITIAL': 'unit-prices-initial-load.csv',
  'HOLDINGS:INITIAL': 'holdings-initial-load.csv',
  'HOLDINGS:DAILY': 'daily-client-holdings.csv',
  'TRANSACTIONS:INITIAL': 'transactions-initial-load.csv',
  'TRANSACTIONS:DAILY': 'daily-transactions.csv',
  'WM_HOLDINGS:INITIAL': 'wm-holdings-initial-load.csv',
  'WM_HOLDINGS:DAILY': 'wm-holdings-daily.csv',
  'WM_TRANSACTIONS:INITIAL': 'wm-transactions-initial-load.csv',
  'WM_TRANSACTIONS:DAILY': 'wm-transactions-daily.csv',
  BENCHMARK: 'daily-benchmark.csv',
  INCOME_EXPENSE: 'income-expense-breakdown.csv',
  ...Object.fromEntries(SECURITIES_CLASSES.map((c) => [c, fundOf(c).toLowerCase() + '-security-holdings.csv'])),
  ...Object.fromEntries(FUND_FACTS_CLASSES.map((c) => [c, fundOf(c).toLowerCase() + '-fund-facts.csv'])),
});

/** The key a class and kind are templated under: the class alone for a class with no kind, as the registry keys them. */
export function templateKey(fileClass, loadKind) {
  if (!takesLoadKind(fileClass)) return fileClass;
  return loadKind ? fileClass + ':' + loadKind : null;
}

/**
 * The template of a class and kind - { fileName, text }, the header row and a
 * line end - or null when the class needs a kind and has none yet.
 */
export function templateFor(fileClass, loadKind) {
  const key = templateKey(fileClass, loadKind);
  const columns = key ? TEMPLATE_COLUMNS[key] : null;
  if (!columns) return null;
  return { fileName: TEMPLATE_NAMES[key], text: columns.join(',') + '\r\n' };
}

/**
 * The "Download template" menu: every class and kind this person may upload,
 * each with its key and label - an initial load's only with core.intake.initial
 * (SCREEN_RULES uploads.initial), as the dialog offers the kind, and a fund's
 * security holdings and fact sheet only for the funds given (stagedFiles.js
 * uploadFundIds), as the File category offers them.
 *
 * @param {boolean} canInitial
 * @param {string[]|null} [fundIds] null: every fund's
 * @returns {{ key: string, label: string, fileClass: string, loadKind: string|null }[]}
 */
export function templateChoices(canInitial, fundIds = null) {
  return Object.keys(TEMPLATE_COLUMNS)
    .map((key) => {
      const [fileClass, loadKind = null] = key.split(':');
      return { key, fileClass, loadKind };
    })
    .filter((t) => t.loadKind !== 'INITIAL' || canInitial)
    .filter((t) => fundIds === null || fundOf(t.fileClass) === null || fundIds.includes(fundOf(t.fileClass)))
    // In the File category's order, each class's daily file before its initial load, as the dialog offers the kinds.
    .sort((a, b) => FILE_CLASS_KEYS.indexOf(a.fileClass) - FILE_CLASS_KEYS.indexOf(b.fileClass) || KIND_ORDER.indexOf(a.loadKind) - KIND_ORDER.indexOf(b.loadKind))
    .map((t) => ({
      ...t,
      label: (TEMPLATE_LABEL[t.fileClass] ?? CLASS_LABEL[t.fileClass]) + (t.loadKind ? (t.loadKind === 'DAILY' ? ' - daily' : ' - initial load') : ''),
    }));
}

/**
 * The "Download template" menu by the File category's groups (uploadKinds.js
 * CLASS_GROUPS), each with the templateChoices it holds - a group with none
 * left out - so the menu reads as the category dropdown does.
 *
 * @param {boolean} canInitial
 * @param {string[]|null} [fundIds]
 * @returns {{ label: string, choices: ReturnType<typeof templateChoices> }[]}
 */
export function templateGroups(canInitial, fundIds = null) {
  const choices = templateChoices(canInitial, fundIds);
  return CLASS_GROUPS
    .map((group) => ({ label: group.label, choices: choices.filter((t) => groupOf(t.fileClass) === group.label) }))
    .filter((group) => group.choices.length > 0);
}

const KIND_ORDER = Object.freeze([null, 'DAILY', 'INITIAL']);

/** The menu's words for a class, where they are not the File category's (uploadKinds.js CLASS_LABEL). */
const TEMPLATE_LABEL = Object.freeze({
  NAV_VALUES: 'NAV values',
  BANK_STATEMENT: 'Bank statement',
  CALENDAR: 'Calendar',
  UNIT_PRICES: 'Unit prices',
  HOLDINGS: 'Holdings',
  TRANSACTIONS: 'Transactions',
  WM_HOLDINGS: 'WM Holdings',
  WM_TRANSACTIONS: 'WM Transactions',
});
